import { useState } from 'react'
import { isAxiosError } from 'axios'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { LoadingMessage } from '@/components/ui/LoadingMessage'
import { Button } from '@/components/ui/Button'
import { CustomCardForm, EMPTY_CARD_VALUES } from './CustomCardForm'
import type { CustomCardValues } from './CustomCardForm'
import { SuggestionReviewForm } from './SuggestionReviewForm'
import {
  lookupWord, suggestWord, createWordFromSuggestion, trackWord, setWordStrength, fetchWord,
} from '@/api/words'
import { useCreateCustomFlashcard } from '@/hooks/useCreateCustomFlashcard'
import { cn } from '@/utils/cn'
import { KNOWN_LEVELS } from '@/utils/strengthLevels'
import type { Word, WordSuggestion, KnownLevel } from '@/types/word'

type Phase = 'search' | 'results' | 'notFound' | 'review' | 'custom'

const resultButtonClasses = cn(
  'shrink-0 px-4 py-2 rounded-full text-xs font-light min-h-11',
  'border border-border-divider text-accent hover:bg-surface-bg',
  'transition-colors duration-200 disabled:opacity-50',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
)

const linkButtonClasses = cn(
  'self-start text-xs text-muted font-light hover:text-accent min-h-11',
  'transition-colors duration-200',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded',
)

const choiceButtonClasses = 'w-full sm:w-auto px-6 py-2.5 min-h-11'

function sameSuggestion(a: WordSuggestion, b: WordSuggestion): boolean {
  return a.lemma === b.lemma && a.part_of_speech === b.part_of_speech && a.gender === b.gender
    && a.definition === b.definition && a.translations.join('\n') === b.translations.join('\n')
}

function errorsFrom(err: unknown): Record<string, string[]> {
  if (isAxiosError(err) && err.response?.data?.errors) {
    return err.response.data.errors as Record<string, string[]>
  }
  return { base: ['Something went wrong. Please try again.'] }
}

/** Track / know actions for one search result — "I know this" opens the two levels. */
function ResultActions({ word, tracked, mastered, onTrack, onKnown }: {
  word: Word
  tracked: boolean
  mastered: boolean
  onTrack: (word: Word) => void
  onKnown: (word: Word, level: KnownLevel) => void
}) {
  const [choosingLevel, setChoosingLevel] = useState(false)

  if (mastered) {
    return <span className="shrink-0 text-xs text-muted font-light">Mastered</span>
  }

  if (choosingLevel) {
    return (
      <div className="shrink-0 flex items-center gap-2">
        {KNOWN_LEVELS.map(({ level, label, hint }) => (
          <button
            key={level}
            type="button"
            title={hint}
            onClick={() => { setChoosingLevel(false); onKnown(word, level) }}
            className={resultButtonClasses}
          >
            {label}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="shrink-0 flex items-center gap-2">
      {tracked ? (
        <span className="text-xs text-muted font-light">Tracked</span>
      ) : (
        <button type="button" onClick={() => onTrack(word)} className={resultButtonClasses}>
          Track
        </button>
      )}
      <button type="button" onClick={() => setChoosingLevel(true)} className={resultButtonClasses}>
        I know this
      </button>
    </div>
  )
}

interface AddWordModalProps {
  languageId: number
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export function AddWordModal({ languageId, open, onClose, onSuccess }: AddWordModalProps) {
  const [phase, setPhase] = useState<Phase>('search')
  const [term, setTerm] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState<Word[]>([])
  // Results shown for a word other than the one typed: the AI's correction, or the
  // dictionary's own entry for a word the learner approved. Tracking uses its lemma.
  const [redirect, setRedirect] = useState<{ lemma: string; existing: boolean } | null>(null)
  const [suggestion, setSuggestion] = useState<WordSuggestion | null>(null)
  const [checking, setChecking] = useState(false)
  const [aiMiss, setAiMiss] = useState(false)
  const [aiError, setAiError] = useState(false)
  const [approving, setApproving] = useState(false)
  const [approveErrors, setApproveErrors] = useState<Record<string, string[]>>({})
  const [trackedIds, setTrackedIds] = useState<Set<number>>(new Set())
  const [masteredIds, setMasteredIds] = useState<Set<number>>(new Set())
  const { create, submitting, errors } = useCreateCustomFlashcard(() => {
    onSuccess()
    handleClose()
  })

  function reset() {
    setPhase('search')
    setTerm('')
    setResults([])
    setRedirect(null)
    setSuggestion(null)
    setAiMiss(false)
    setAiError(false)
    setApproveErrors({})
    setTrackedIds(new Set())
    setMasteredIds(new Set())
  }

  function handleClose() {
    reset()
    onClose()
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    const query = term.trim()
    if (!query || searching) return

    setSearching(true)
    // Dictionary only: a hand-typed word may be a typo, so the AI runs only when asked.
    lookupWord(languageId, query, query, true)
      .then((res) => {
        const words = res.data.data.words ?? []
        if (res.data.data.found && words.length > 0) {
          setResults(words)
          setPhase('results')
        } else {
          setPhase('notFound')
        }
      })
      .catch(() => setPhase('notFound'))
      .finally(() => setSearching(false))
  }

  function handleCheckWithAi() {
    setChecking(true)
    setAiError(false)
    suggestWord(languageId, term.trim())
      .then((res) => {
        const data = res.data.data
        if (data.found && data.words && data.words.length > 0) {
          showResults(data.words, data.did_you_mean ? { lemma: data.did_you_mean, existing: false } : null)
        } else if (data.suggestion) {
          setSuggestion(data.suggestion)
          setPhase('review')
        } else {
          setAiMiss(true)
        }
      })
      .catch(() => setAiError(true))
      .finally(() => setChecking(false))
  }

  function showResults(words: Word[], shownFor: typeof redirect) {
    setResults(words)
    setRedirect(shownFor)
    setPhase('results')
  }

  // The lexicon already had the approved word, so the learner's version was not
  // stored: show them the real entry to track rather than silently tracking it.
  // Looked up by lemma for the full interpretations; by id when no stored form spells
  // the lemma (e.g. a reading click saved only an inflected form).
  function showExisting(id: number, lemma: string) {
    return lookupWord(languageId, lemma, lemma, true)
      .then((res) => {
        const words = res.data.data.words ?? []
        return words.length > 0 ? words : fetchWord(languageId, id).then((w) => [w.data.data])
      })
      .then((words) => showResults(words, { lemma, existing: true }))
  }

  function handleApprove(approved: WordSuggestion) {
    setApproving(true)
    setApproveErrors({})
    const edited = suggestion ? !sameSuggestion(approved, suggestion) : true
    createWordFromSuggestion(languageId, approved, edited)
      .then((res) => {
        const { id, lemma } = res.data.data
        if (res.data.existing) return showExisting(id, lemma)
        return trackWord(languageId, id, lemma, '').then(() => {
          onSuccess()
          handleClose()
        })
      })
      .catch((err: unknown) => setApproveErrors(errorsFrom(err)))
      .finally(() => setApproving(false))
  }

  /** The form to record as tracked: what the results were shown for, not a typo. */
  function surface() {
    return redirect?.lemma ?? term.trim()
  }

  function handleTrack(word: Word) {
    trackWord(languageId, word.id, surface(), '')
      .then(() => {
        setTrackedIds((prev) => new Set(prev).add(word.id))
        onSuccess()
      })
      .catch(() => undefined)
  }

  function handleKnown(word: Word, level: KnownLevel) {
    const tracked = trackedIds.has(word.id) || Boolean(word.tracked)
    const request = tracked
      ? setWordStrength(languageId, word.id, level)
      : trackWord(languageId, word.id, surface(), '', level)

    request
      .then(() => {
        setTrackedIds((prev) => new Set(prev).add(word.id))
        if (level === 'mastered') setMasteredIds((prev) => new Set(prev).add(word.id))
        onSuccess()
      })
      .catch(() => undefined)
  }

  function handleCreate(values: CustomCardValues) {
    create({
      language_id: languageId,
      lemma: values.lemma,
      translation: values.translation,
      part_of_speech: values.part_of_speech || null,
      gender: values.gender || null,
      example_sentence: values.example_sentence || null,
      notes: values.notes || null,
    })
  }

  return (
    <Modal open={open} onClose={handleClose} title="Add word">
      {phase === 'search' && (
        <form onSubmit={handleSearch} className="flex flex-col gap-4">
          <p className="text-sm text-muted font-light leading-relaxed">
            Search the dictionary first — if the word is there, tracking it gives you
            its full senses and forms.
          </p>
          <Input
            id="add-word-term"
            label="Word"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            autoFocus
            required
          />
          {searching ? (
            <LoadingMessage className="py-2">Searching dictionary…</LoadingMessage>
          ) : (
            <button
              type="submit"
              className={cn(
                'self-end px-6 py-2.5 rounded-full text-sm font-light tracking-wide min-h-11',
                'bg-btn-filled-bg text-btn-filled-text hover:bg-btn-filled-hover-bg',
                'transition-colors duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
              )}
            >
              Search
            </button>
          )}
        </form>
      )}

      {phase === 'results' && (
        <div className="flex flex-col gap-4">
          {redirect && (
            <p className="text-sm text-muted font-light leading-relaxed">
              {redirect.existing ? (
                <><span className="font-serif text-heading">{redirect.lemma}</span> already has an entry — track it from here.</>
              ) : (
                <>Did you mean <span className="font-serif text-heading">{redirect.lemma}</span>? It is in the dictionary.</>
              )}
            </p>
          )}
          <ul className="flex flex-col gap-2">
            {results.map((word) => (
              <li
                key={word.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border-divider px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="font-serif text-base font-medium text-heading truncate">
                    {word.lemma}
                    <span className="ml-2 text-xs text-muted font-sans font-light">
                      {word.part_of_speech}
                    </span>
                  </p>
                  <p className="text-xs text-muted font-light truncate">
                    {word.senses.flatMap((s) => s.translations.en ?? []).slice(0, 3).join(', ')}
                  </p>
                </div>
                <ResultActions
                  word={word}
                  tracked={trackedIds.has(word.id) || Boolean(word.tracked)}
                  mastered={masteredIds.has(word.id) || Boolean(word.known)}
                  onTrack={handleTrack}
                  onKnown={handleKnown}
                />
              </li>
            ))}
          </ul>

          <button type="button" onClick={() => setPhase('custom')} className={linkButtonClasses}>
            Not what you meant? Create your own card
          </button>
        </div>
      )}

      {phase === 'notFound' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted font-light leading-relaxed" aria-live="polite">
            {aiMiss
              ? 'The AI couldn’t tell what word this is either. Check the spelling, or create your own card — it stays private to you.'
              : aiError
                ? 'Couldn’t reach the AI just now. Try again, or create your own card.'
                : 'Not in the dictionary. The AI can suggest a spelling and meaning for you to check — nothing is saved until you approve it.'}
          </p>
          {checking ? (
            <LoadingMessage className="py-2">Asking the AI…</LoadingMessage>
          ) : (
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button variant="quiet" onClick={() => setPhase('custom')} className={choiceButtonClasses}>
                Create your own card
              </Button>
              {!aiMiss && (
                <Button onClick={handleCheckWithAi} className={choiceButtonClasses}>Check with AI</Button>
              )}
            </div>
          )}
        </div>
      )}

      {phase === 'review' && suggestion && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted font-light leading-relaxed">
            The AI’s suggestion — correct anything that’s off. Once added, the word joins the
            shared dictionary and its forms are mapped out.
          </p>
          <SuggestionReviewForm
            initial={suggestion}
            submitting={approving}
            errors={approveErrors}
            onSubmit={handleApprove}
            footer={(
              <button type="button" onClick={() => setPhase('custom')} className={linkButtonClasses}>
                Create a private card instead
              </button>
            )}
          />
        </div>
      )}

      {phase === 'custom' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted font-light leading-relaxed">
            Your own card stays private to you.
          </p>
          <CustomCardForm
            initial={{ ...EMPTY_CARD_VALUES, lemma: term.trim() }}
            submitLabel="Add card"
            submitting={submitting}
            errors={errors}
            onSubmit={handleCreate}
          />
        </div>
      )}
    </Modal>
  )
}
